import { useMemo } from "react";
import { Icon } from "../../kit";
import type { TabId } from "../inspector/schema";
import { filterAllTabs } from "../inspector/search";
import { ALL_GROUP_SECTIONS } from "../sections";
import { useUi } from "../../../state/editor/useEditor";
import { RIGHT_RAIL } from "./railConfig";
import { RAIL_W } from "../layout";
import { ScrollArea } from "../ScrollArea";
import { AI_PANEL, PINNED_PANEL, reopenTarget } from "../basic/mode";

/**
 * Advanced mode's right rail: Pinned, AI Look Match, then our ten groups
 * (sections/groups.ts — regrouped by what you are doing, 2026-09-29), every one
 * with its word under the icon. While a search is typed, each group shows how
 * many settings in it match. The Basic | Advanced switch is in the top bar;
 * Basic mode has no right rail at all, just its Fine-tune panel.
 *
 * Clicking the active tab collapses the panel; the chevron re-opens the last.
 */
function searchCounts(query: string, experimental: boolean) {
  const sources = ALL_GROUP_SECTIONS.map((g) => ({ tabId: g.tabId, sections: g.sections.filter((s) => experimental || !s.experimental) }));
  return filterAllTabs(sources, query).counts;
}

export function RightRail() {
  const [ui, patchUi] = useUi();

  // While searching, each group shows how many settings in it match.
  const counts = useMemo(() => (ui.search.trim() ? searchCounts(ui.search, ui.showExperimental) : null), [ui.search, ui.showExperimental]);

  const railButton = (id: string, icon: string, label: string, onOpen?: () => void) => {
    const active = ui.rightTab === id;
    const n = counts ? counts[id as TabId] ?? 0 : null;
    return (
      <button
        key={id}
        title={n != null ? `${label}: ${n} matching` : undefined}
        onClick={() => {
          if (active) patchUi({ rightTab: null, lastRightTab: id });
          else {
            patchUi({ rightTab: id, lastRightTab: id });
            onOpen?.();
          }
        }}
        aria-pressed={active}
        className={`flex min-h-[48px] w-full shrink-0 flex-col items-center gap-0.5 rounded-[10px] px-0 py-1.5 transition-colors [@media(max-height:860px)]:min-h-[40px] [@media(max-height:860px)]:gap-0 [@media(max-height:860px)]:py-1 ${
          active ? "bg-ed-active text-ed-text" : "text-ed-text-2 hover:bg-ed-hover hover:text-ed-text"
        }`}
        data-right-tab={id}
      >
        <span className="relative">
          <Icon name={icon} size={20} />
          {n ? (
            <span className="absolute -right-3 -top-1.5 min-w-[18px] rounded-full bg-ed-text px-1 text-center text-[11px] font-semibold leading-[16px] text-white" aria-label={`${n} matching`}>
              {n}
            </span>
          ) : null}
        </span>
        <span className={`w-full text-center text-[11px] leading-[13px] tracking-[-0.01em] [hyphens:auto] [overflow-wrap:anywhere] ${active ? "font-semibold" : "font-medium"}`}>{label}</span>
      </button>
    );
  };

  return (
    <nav
      aria-label="Advanced settings"
      className="pointer-events-auto absolute right-0 top-0 bottom-0 z-20 flex flex-col border-l border-ed-border bg-ed-panel px-1 py-2.5"
      style={{ width: RAIL_W }}
    >
      {/* Scrolls on a short window, with a fade on whichever end has more —
          at 640px tall "Tools" sat below the fold with nothing to say so. */}
      <ScrollArea bar="none" className="flex flex-col gap-0.5">
        {/* AI Look Match writes the same look fields in either mode. */}
        {railButton(AI_PANEL, "auto_awesome", "AI Match")}
        {railButton(PINNED_PANEL, "star", "Pinned", () => patchUi({ rightTab: PINNED_PANEL, lastRightTab: PINNED_PANEL, lastAdvancedTab: PINNED_PANEL }))}
        <div className="mx-2 my-1 h-px shrink-0 bg-ed-border" />
        {RIGHT_RAIL.map((item) =>
          railButton(item.id, item.icon, item.label, () => patchUi({ rightTab: item.id, lastRightTab: item.id, lastAdvancedTab: item.id })),
        )}
      </ScrollArea>
      <button
        title={ui.rightTab ? "Hide panel" : "Show panel"}
        aria-label={ui.rightTab ? "Hide panel" : "Show panel"}
        onClick={() =>
          patchUi(ui.rightTab ? { rightTab: null, lastRightTab: ui.rightTab } : { rightTab: reopenTarget(ui) })
        }
        className="mt-1 flex h-9 w-full shrink-0 items-center justify-center rounded-lg text-ed-text-3 transition-colors hover:bg-ed-hover hover:text-ed-text"
      >
        <Icon name={ui.rightTab ? "right_panel_close" : "right_panel_open"} size={20} />
      </button>
    </nav>
  );
}
